import { useEffect, useRef, useState } from "react";
import { Button, DatePicker, Input, Table, Spin, message } from "antd";
import dayjs from "dayjs";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import locale from "antd/es/date-picker/locale/zh_CN";
import reconcile_icon from "@/images/service/reconcile_icon.png";
import empty_png from "@/images/empty.png";
import "./index.less";

import "dayjs/locale/zh-cn";
import { useUserInfo } from "@/model";
import { commonApi, serviceApi } from "@/apis";
import { downloadFile, strFormatNumber } from "@/utils";

const Invoice = () => {
  const userInfo = useUserInfo();
  const downLoading = useRef(false);
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<InvoiceFindByConditionsItem[]>([]);
  const [dictCodes, setDictCodes] = useState<{ [key: string]: any }>({});
  // 搜索条件
  const [orderNum, setOrderNum] = useState("");
  const [orderTime, setOrderTime] = useState<any>("");

  // 表格信息
  const [curPage, setCurPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    commonApi
      .getDictionaryList({
        dictTypeCodeList: ["dms_invoice_type01"],
      })
      .then((res) => {
        const list = res.dms_invoice_type01 || [];
        const _dictCodes: { [key: string]: any } = {};
        list.forEach(({ dictCode, dictDesc }) => {
          _dictCodes[dictCode] = dictDesc;
        });
        setDictCodes(_dictCodes);
      })
      .catch((error) => {
        message.error(error);
      });
  }, []);

  useEffect(() => {
    if (userInfo.curUserInfo) {
      handleSearch({
        account: userInfo.curUserInfo.userAccount,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo.curUserInfo]);

  const handleSearch = async (data: any = {}) => {
    try {
      setLoading(true);
      const kprq = orderTime && dayjs(orderTime).format("YYYYMM");
      const res = await serviceApi.invoice_findByConditions({
        account: userInfo.curUserInfo?.userAccount,
        fphm: orderNum,
        kprq,
        size: 20,
        page: curPage,
        ...data,
      });
      const { total, records } = res;
      setTotal(total);
      setTableData(records);
    } catch (error: any) {
      message.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleInquire = () => {
    setCurPage(1);
    handleSearch({ page: 1 });
  };

  const handleReset = () => {
    setOrderNum("");
    setOrderTime("");
    setCurPage(1);
    handleSearch({
      fphm: "",
      kprq: "",
      page: 1,
    });
  };

  const changePage = (page: number) => {
    setCurPage(page);
    handleSearch({
      page,
    });
  };

  const downloadInvoice = (dpid: string, id: string) => {
    if (downLoading.current) return;
    downLoading.current = true;
    setTimeout(() => {
      downLoading.current = false;
    }, 2000);
    downloadFile(
      `https://crm.yingde.com/crm-bridge/v1/push/po/getDownloadInvoice?dpid=${dpid}`,
      "发票.pdf"
    );
    serviceApi
      .invoice_setRead(id)
      .then((res) => {
        console.log(res);
      })
      .catch((error) => {
        message.error(error);
      });
  };

  const columns: ColumnsType<InvoiceFindByConditionsItem> = [
    {
      title: "开票月份",
      dataIndex: "ssyf",
      key: "ssyf",
    },
    {
      title: "开票日期",
      dataIndex: "kprq",
      key: "kprq",
    },
    {
      title: "开票号码",
      dataIndex: "fphm",
      key: "fphm",
    },
    {
      title: "开票金额(元)",
      dataIndex: "hjje",
      key: "hjje",
      render: (_, record) => (
        <span>
          {strFormatNumber(
            (Number(record.hjje || 0) + Number(record.hjse || 0)).toFixed(2)
          )}
        </span>
      ),
    },
    {
      title: "是否下载",
      dataIndex: "isRead",
      key: "isRead",
      render: (isRead) => <span>{isRead ? "已下载" : "未下载"}</span>,
    },
    {
      title: "发票类型",
      dataIndex: "fplx",
      key: "fplx",
      render: (fplx) => <span>{dictCodes[fplx]}</span>,
    },
    {
      title: "操作",
      key: "id",
      render: (_, record) =>
        record.dpid ? (
          // eslint-disable-next-line jsx-a11y/anchor-is-valid
          <a
            onClick={() => downloadInvoice(record.dpid || "", record.id || "")}
          >
            下载
          </a>
        ) : (
          <></>
        ),
    },
  ];

  return (
    <div className="invoice-wrapper">
      <div className="center-main-title">
        <img src={reconcile_icon} alt="" />
        <h3>对账函</h3>
      </div>
      <div className="invoice-table-wrap">
        <Spin spinning={loading}>
          <article className="flex-b-c search-wrapper">
            <div className="flex">
              <Input
                value={orderNum}
                onChange={(e) => setOrderNum(e.target.value)}
                className="invoice-number"
                allowClear
                placeholder="开票号码"
              />
              <DatePicker
                placeholder="开票月份"
                value={orderTime}
                locale={locale}
                className="invoice-time"
                onChange={(e: any) => setOrderTime(e)}
                picker="month"
              />
            </div>
            <div className="btns">
              <Button icon={<ReloadOutlined />} onClick={handleReset}>
                重置
              </Button>
              <Button
                className="inquire-btn"
                icon={<SearchOutlined />}
                type="primary"
                onClick={handleInquire}
              >
                查询
              </Button>
            </div>
          </article>
          <Table
            columns={columns}
            dataSource={tableData}
            className="invoice-table"
            locale={{
              emptyText: (
                <div style={{ marginBottom: 30 }}>
                  <img src={empty_png} alt="暂无数据" />
                  <p>暂无数据</p>
                </div>
              ),
            }}
            pagination={{
              current: curPage,
              total,
              defaultPageSize: 10,
              onChange: changePage,
            }}
          />
        </Spin>
      </div>
    </div>
  );
};

export default Invoice;
